import {useParams} from "react-router";
import React, {useEffect, useMemo, useState} from "react";
import {useTableObject} from "../../../utils/hooks/useTableObject";
import {useMutation, useQuery} from "react-query";
import {queryKeysGuide} from "../../../static/data/queryKeys";
import {api} from "../../../api/api";
import {openNotification} from "../../../utils/jsxUtils";
import {errorNotification, invalidateQueries, queryOptionsPreset} from "../../../utils/rest";
import TableActionButtons from "../../../components/ui/TableObjects/components/TableActionButtons/TableActionButtons";
import {getSelectedItems} from "../../../utils/parsers";
import useScrollMount from "../../../utils/hooks/useScrollMount";
import NavbarLayout from "../../../components/layout/NavbarLayout/NavbarLayout";
import FieldHelper from "../../../components/common/FieldHelper/FieldHelper";
import SoloCollapse from "../../../components/ui/SoloCollapse/SoloCollapse";
import {addCollapseIcon, editCollapseIcon} from "../../../components/ui/SoloCollapse/components/icons";
import GuideAddForm from "./components/GuideAddForm/GuideAddForm";
import TableObjects from "../../../components/ui/TableObjects/TableObjects";
import {GuideObj, ObjDB} from "../../../types/apiTypes";
import {guideHelpers} from "./formData/guideFieldHelper";
import GuideEditForm from "./components/GuideEditForm/GuideEditForm";
import {DraggableBodyRow, DraggableContainer, DragHandle} from "../../../components/table/dragTable/dragTable";
import {SortableContainerProps} from "react-sortable-hoc";
import arrayMove from "array-move";
import {notification} from "antd";
import { sortDataByFieldIdWithoutFilter} from "../../../utils/table";

type TableItem = ObjDB<GuideObj>

interface GuidePageProps {}

const GuidePage: React.FC<React.PropsWithChildren<GuidePageProps>> = () => {
    const [openedAddForm, setOpenedAddForm] = useState<boolean>(false)
    const [openedEditForm, setOpenedEditForm] = useState<boolean>(false)
    const [editItem, setEditItem] = useState<TableItem | null>(null)
    const [allData, setAllData] = useState<TableItem[]>([])
    const [tableHeadLoader, setTableHeadLoader] = useState<boolean>(false)
    const [shouldShowSortNote, setShouldShowSortNote] = useState<boolean>(true)

    const params = useParams<{ id: string }>()

    const {
        selectedRowKeys,
        searchText,
        setSelectedRowKeys,
        getColumnSearchProps,
    } = useTableObject({initialSearchText: params.id})

    const queryAll = useQuery<undefined, Error, TableItem[]>(
        queryKeysGuide[0],
        api.guide.getAll,
        {
            onError(err: Error) {
                console.log('ERROR get all objects', err)
                openNotification(
                    'error',
                    'Ошибка получения списка объектов',
                    err.message
                )
            },
            ...queryOptionsPreset,
        }
    )

    const sortAllMutate = useMutation(
        api.guide.sortMany.bind(api.guide),
        {
            mutationKey: 'guides-sort-all',
            onSuccess() {
                invalidateQueries(queryKeysGuide[0])
                openNotification(
                    'success',
                    'Сортировка выполнена успешно'
                )
            },
            onError: (err: Error) => errorNotification(err, 'Ошибка при сортировке объектов'),
            onSettled() { setTableHeadLoader(false) },
            onMutate() { setTableHeadLoader(true) }
        }
    )

    const queryDelete = useMutation(
        'guide-delete',
        api.guide.delete,
        {
            onSuccess() {
                openNotification('success', 'Объект успешно удален')
                invalidateQueries(queryKeysGuide[0])
            },
            onError(err: Error) {
                console.error("Error delete obj: ", err.message)
                openNotification('error', 'Ошибка при удалении объекта', err.message)
            },
        } as any
    )
     // ColumnsType<TableItem>
    const columns: any = useMemo(() => {
        return [
            {
                title: '№',
                dataIndex: 'sort',
                width: 30,
                className: 'drag-visible',
                render: () => <DragHandle />,
            },
            {
                title: 'ID',
                dataIndex: 'id',
                sorter: {
                    compare: (a: TableItem, b: TableItem) => a.id - b.id,
                },
                ...getColumnSearchProps('id'),
                defaultFilteredValue: params.id === searchText ? [params.id] : undefined,
            },
            {
                title: 'Название',
                dataIndex: 'name',
                sorter: {
                    compare: (a, b) => a.name.localeCompare(b.name),
                },
                ...getColumnSearchProps('name'),
            },
            {
                title: 'Путь',
                dataIndex: 'path',
                sorter: {
                    compare: (a, b) => a.path.localeCompare(b.path),
                },
                ...getColumnSearchProps('path'),
            },
            {
                title: 'Индекс (сортировка)',
                dataIndex: 'number',
                render: (val: number) => val,
                width: 130,
            },
            {
                title: 'Текст',
                dataIndex: 'text',
                sorter: {
                    compare: (a, b) => a.text.localeCompare(b.text),
                },
                ...getColumnSearchProps('text'),
                render: () => {
                    return <div>-</div>
                }
            },
            {
                title: 'Действие',
                dataIndex: '',
                align: 'right',
                render: (value: TableItem) => (
                    <TableActionButtons
                        onEdit={() => handleEditItem(value)}
                        onDelete={() => queryDelete.mutate(value)}
                    />
                )
            },
        ]
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getColumnSearchProps])

    const onSelectChange = (selectedRowKeys: React.Key[]) => {
        setSelectedRowKeys(selectedRowKeys)
    }

    const handleTableAdd = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
        setOpenedAddForm(true)
    }
    const showSortNote = () => {
        if (shouldShowSortNote) {
            notification.open({
                message: 'Для сохранения последовательности элементов нажмите иконку "№" в заголовке таблицы',
                placement: 'bottomRight',
                onClose: () => setShouldShowSortNote(false)
            })
        }
    }
    const onSortEnd: SortableContainerProps['onSortEnd'] = ({ oldIndex, newIndex }) => {
        if (oldIndex !== newIndex) {
            showSortNote()
            const newData = arrayMove([...allData], oldIndex, newIndex).filter(el => !!el);
            setAllData(newData)
        }
    };

    const handleEditItem = (value: TableItem) => {
        setEditItem(value)
        setOpenedEditForm(true)
        setOpenedAddForm(false)
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }
    const handleSaveSort = async () => {
        sortAllMutate.mutate(allData)
    }

    const handleTableDelete = async () => {
        try {
            setTableHeadLoader(true)
            const deleteItems = getSelectedItems<TableItem>(selectedRowKeys, allData || [])
            await api.guide.deleteMany(deleteItems)
            invalidateQueries(queryKeysGuide[0])
            openNotification(
                'success',
                'Выбранные объекты успешно удалены'
            )
            setSelectedRowKeys([])
        } catch (err: any) {
            console.error('Error delete many', err)
            openNotification(
                'error',
                'Ошибка при удалении объектов',
                err.message
            )
        } finally { setTableHeadLoader(false) }
    }

    useEffect(() => {
        const responseData = queryAll.data
        if (Array.isArray(responseData)) {
            setAllData(sortDataByFieldIdWithoutFilter(responseData))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryAll.data])

    const handleTableRefresh = () => invalidateQueries(queryKeysGuide)
    useScrollMount()

    console.log('data', allData)

    return (
        <NavbarLayout>
            <section className="uif-goal-page">
                <FieldHelper
                    content={guideHelpers.pageTitle}
                    text="Справочники | Guides"
                    isPageTitle
                />

                <div className="uif-guide-page__collapses">
                    <SoloCollapse
                        header="Создать новый объект"
                        keyCollapse="add"
                        show={openedAddForm}
                        icon={addCollapseIcon}
                        setShow={setOpenedAddForm}
                    >
                        <GuideAddForm setOpenedForm={setOpenedAddForm} itemLength={allData.length} />
                    </SoloCollapse>
                    <SoloCollapse
                        header="Редактировать объект"
                        keyCollapse="edit"
                        show={openedEditForm}
                        icon={editCollapseIcon}
                        setShow={setOpenedEditForm}

                    >
                        <GuideEditForm
                            editItem={editItem}
                            setOpenedForm={setOpenedEditForm}
                            setEditItem={setEditItem}
                        />
                    </SoloCollapse>
                </div>

                <TableObjects
                    selectedRowKeys={selectedRowKeys}
                    count={allData.length}
                    columns={columns}
                    dataSource={allData}
                    rowSelection={{
                        selectedRowKeys: selectedRowKeys,
                        onChange: onSelectChange
                    }}
                    components={{
                        body: {
                            wrapper: DraggableContainer(onSortEnd),
                            row: DraggableBodyRow(allData),
                        },
                    }}
                    onDelete={handleTableDelete}
                    onAdd={handleTableAdd}
                    onRefresh={handleTableRefresh}
                    onSaveSort={handleSaveSort}
                    rowKey="id"
                    isFetching={queryAll.isFetching}
                    headerLoader={tableHeadLoader}
                    scroll={{x: 500}}
                />
            </section>
        </NavbarLayout >
    )
}

export default GuidePage